import _ from 'lodash';
import { useCallback } from 'react';
import { useChannelContext } from 'scbk-services/channel';
import { Channel } from 'scbk-services/config';
import { EventType, useEventBusEmit } from 'scbk-services/event-bus';
import { useRestContext, useRestPost } from 'scbk-services/http';
import { useIpinside } from 'scbk-services/ipinside';
import { loadingManager } from 'scbk-services/loading';
import { useUserContext } from 'scbk-services/shared-context';
import { MenuItem } from 'scbk-services/types/menu/menu';

/**
 * 메뉴권한체크 요청
 */
interface AutFncAuthorityRequest {
    menuId: string;
    acType?: string;
    forceCheckCode?: string;
    restrictCountryCheckYn?: string;
    securityLevel?: string;
}

/**
 * 메뉴권한체크 응답
 */
interface AutFncAuthorityResponse {
    resultCode: string;
}

interface UtlFncAutCheckServiceTimeRequest {
    forceCheckCode: string;
}

interface UtlFncAutCheckServiceTimeResponse {
    resultCd: string;
}

const checkMenuInfoEndpoint = '/utilities/functions/authority/checkMenuInfo';
const checkServiceTimeEndpoint = '/utilities/functions/authority/checkServiceTime';
/**
 * 메뉴 권한체크 hook 함수
 * @returns
 */
const useMenuAuthority = (forceDisplayPage: boolean = false) => {
    const { getCommonContext } = useUserContext();

    const { channel } = useChannelContext();
    const { updateContext: updateRestContext } = useRestContext();
    const emit = useEventBusEmit();

    const { load } = useIpinside();

    const { request: requestCheckMenuInfo } = useRestPost<
        AutFncAuthorityRequest,
        AutFncAuthorityResponse
    >(
        {
            endpoint: checkMenuInfoEndpoint,
        },
        {
            forceErrorDisplayPage: forceDisplayPage,
            skipLoading: true,
        },
    );

    const { request: requestCheckServiceTime } = useRestPost<
        UtlFncAutCheckServiceTimeRequest,
        UtlFncAutCheckServiceTimeResponse
    >(
        {
            endpoint: checkServiceTimeEndpoint,
        },
        { skipLoading: true },
    );

    /**
     * CDD 블로킹 대상 체크
     * @param menu
     */
    const checkCddBlocking = useCallback(
        async (menu: MenuItem) => {
            const yoBFlg = getCommonContext().yoBFlg; // CDD 블로킹 대상자 여부
            const blockedCDDMenuIdArr = [
                'MA3TRNSTR001', //이체(footer 메뉴)
                'TRNSGL001', //이체(전체 > 이체/납부 > 이체) => TRNSGL001
                'TRNMUL002', //여러통장이체 => TRNMUL002
                'FEXEXC001', // WFX 실시간매매 => FEXEXC001
                'FEXEXC002', //목표환율매매 => FEXEXC002
                'MA3FRNFTR001', //간편해외재송금
                'FEXEXC004', //현찰환전신청 => FEXEXC004
            ];

            // if (import.meta.env.VITE_ENV === 'local') {
            //     return true;
            // }

            if (yoBFlg === '1' && blockedCDDMenuIdArr.some((item) => item === menu.id)) {
                // Event Bus : CDD block
                emit(EventType.CDDBlock, [menu.id]);
                return false;
            }

            return true;
        },
        [getCommonContext],
    );

    /**
     * 외부 url 호출 필요한지 check
     * @param menu 메뉴정보
     */
    const checkExternalLinkMenu = useCallback(async (menu: MenuItem) => {
        if (
            channel === Channel.MB && //모바일 뱅킹일때
            menu?.fields?.TARGET_CHANNEL === '03'
        ) {
            // Event Bus : 외부 url 호출
            emit(EventType.OpenWebBrowser, [menu.id, menu.url]);
            return true;
        }
        return false;
    }, []);

    /**
     * 메뉴이동 전 체크 함수
     * - CDD 블록킹 대상메뉴
     * - 외부링크여부 확인
     * - 접근제어 확인
     * @param menu 메뉴정보
     * @param forceCheckCode 서비스 이용시간 강제 체크코드
     * @returns
     */
    const checkMenuInfo = useCallback(
        async (
            menu: MenuItem,
            forceCheckCode?: string,
            skipMenuAuthority?: boolean,
        ): Promise<boolean | undefined> => {
            //***********************************************
            //메뉴 접근제어 체크
            //***********************************************
            const { id: menuId, fields = {} } = menu;
            const acType = fields ? fields['acCheckType'] : '';
            const restrictCountryCheckYn = fields ? fields['restrictCountryCheckYn'] : 'N';
            const securityLevel = fields ? fields['securityLevel'] : '';

            //********************************************************************
            // api 호출시 header에 menu-id set
            // [2024.04.22 최성돈] acType 추가
            // [2024.07.29 권태민] 위치 변경
            //********************************************************************
            const menuInfo = {
                menuId,
                acType,
                //forceCheckCode는 요청 시 한번만 사용하므로 store에 저장하지 않는다.
                //forceCheckCode,
            };

            if (skipMenuAuthority) {
                updateRestContext({ ...menuInfo });
                return true;
            }

            //***********************************************
            //외부링크메뉴 여부 체크
            //***********************************************
            if (await checkExternalLinkMenu(menu)) {
                return;
            }

            //***********************************************
            //CDD 블록킹 대상메뉴 체크
            //***********************************************
            if (!(await checkCddBlocking(menu))) {
                return;
            }

            const input = {
                menuId,
                acType,
                forceCheckCode,
                restrictCountryCheckYn,
                // securityLevel,
            };

            const res = await requestCheckMenuInfo(input);
            if (!res) {
                return;
            }

            if (securityLevel === '4' && channel === Channel.IB) {
                loadingManager.open();

                const isInstalled = await load();

                loadingManager.close();

                if (!isInstalled) {
                    emit(EventType.ProgramInstallNavigationRequest);
                    return;
                }
            }

            //********************************************************************
            //메뉴정보 저장
            //********************************************************************
            updateRestContext({ ...menuInfo });

            return true;
        },
        [checkCddBlocking, checkExternalLinkMenu],
    );

    /**
     * 서비스 이용시간 체크
     * @param forceCheckCode 이용시간 체크 대상코드
     */
    const checkServiceTime = async (forceCheckCode: string = '') => {
        if (_.isEmpty(forceCheckCode)) {
            return true;
        }

        const res = await requestCheckServiceTime({ forceCheckCode });

        console.log(res);

        return true;
    };

    return {
        checkCddBlocking,
        checkExternalLinkMenu,
        checkMenuInfo,
        checkServiceTime,
    };
};

export { useMenuAuthority };
